// Follow-ups (`/follow-ups`): every open scheduled visit, call, meeting and task the viewer can see, across stores,
// grouped Overdue / Today / Next 7 days / Later. A read-only port of iOS TasksView (Phase 1: completing, moving and
// cancelling stay in the app). Each row names the store and links to its Upcoming tab, with the assignee and the
// due time. Managers filter by rep (iOS `AgendaScope`) and everyone by type; reps get "My follow-ups": only what is
// assigned to them, as on iOS and in the dashboard's "Needs attention" (followUpsModel.visibleFollowUps).
//
// The filters live in the URL query (replace, not push) so Back from a store returns to the same list. A one-minute
// ticker moves items into Overdue as their time passes, as the dashboard does.

import {
  AlarmClock,
  CalendarCheck,
  CalendarDays,
  CalendarRange,
  ChevronDown,
  ListFilter,
  SearchX,
  Sun,
} from 'lucide-react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { ActivityType } from '@/contracts/enums';
import { useActivities, useUsers, useViewer, useVisibleRecords, useVisibleUsers, useVisitResults } from '@/data/hooks';
import { formatGrouped } from '@/domain/format';
import { pickerUsers } from '@/domain/team';
import { byId, sameId } from '@/domain/util';
import { OwnerAvatar, RequireFirstSync, ScreenLoading, useDocumentTitle } from '@/features/common';
import { ACTIVITY_ICONS } from '@/features/stores/activityIcons';
import { NewerAppBadge } from '@/features/stores/NewerAppBadge';
import { useCan } from '@/session/permissions';
import {
  Button,
  DropdownMenu,
  EmptyState,
  IconBadge,
  PageContainer,
  PageHeader,
  cardClass,
  cn,
  useTicker,
  type MenuEntry,
} from '@/ui';
import {
  ACTIVITY_TYPE_PLURALS,
  FOLLOW_UP_GROUP_EMPTY,
  UNASSIGNED_REP,
  filterFollowUps,
  followUpGroups,
  followUpSummary,
  parseRepParam,
  parseTypeParam,
  repFilterLabel,
  repOptions,
  typeFilterLabel,
  typeOptions,
  visibleFollowUps,
  type FollowUpFilter,
  type FollowUpGroup,
  type FollowUpGroupId,
  type FollowUpRow,
  type RepOption,
} from './followUpsModel';

/** Each group renders this many rows at a time (a busy team can have hundreds scheduled further out). */
const GROUP_PAGE = 100;

const GROUP_LOOK: Readonly<Record<FollowUpGroupId, { icon: ReactNode; color: string }>> = {
  overdue: { icon: <AlarmClock />, color: '#DB4038' },
  today: { icon: <Sun />, color: '#F96A3C' },
  week: { icon: <CalendarDays />, color: '#2673E6' },
  later: { icon: <CalendarRange />, color: '#102A5E' },
};

export default function FollowUpsPage() {
  const can = useCan();
  const title = can.isRep ? 'My follow-ups' : 'Follow-ups';
  useDocumentTitle(title);
  return (
    <PageContainer width="default">
      <RequireFirstSync what={title} fallback={<ScreenLoading layout="list" count={5} label="Loading follow-ups…" />}>
        <FollowUpList title={title} />
      </RequireFirstSync>
    </PageContainer>
  );
}

function FollowUpList({ title }: { title: string }) {
  const can = useCan();
  const viewer = useViewer();
  const activities = useActivities();
  // Names and links come from the stores the viewer can open (archived included, as the store page allows).
  const stores = useVisibleRecords({ includeArchived: true });
  const users = useUsers();
  const visibleUsers = useVisibleUsers();
  const visitResults = useVisitResults();
  const minute = useTicker(60_000);
  const now = useMemo(() => {
    void minute;
    return new Date();
  }, [minute]);

  const [params, setParams] = useSearchParams();
  // Reps have no rep filter: their replica is already only what they may see.
  const rep = can.isManager ? parseRepParam(params.get('rep')) : null;
  const type = parseTypeParam(params.get('type'));
  const filter = useMemo<FollowUpFilter>(() => ({ rep, type }), [rep, type]);
  const filtered = rep !== null || type !== null;
  const update = (patch: { rep?: string | null; type?: ActivityType | null }) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === undefined) next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };
  const clearFilters = () => update({ rep: null, type: null });

  const usersById = useMemo(() => byId(users.values), [users]);
  const resultsById = useMemo(() => byId(visitResults.values), [visitResults]);
  const store = useCallback((id: string) => stores.get(id)?.value, [stores]);

  // What is in scope changes with the data only; the clock just regroups the (much shorter) scheduled list.
  const viewerId = viewer?.profileId;
  const self = can.isRep;
  const inScope = useMemo(
    () =>
      visibleFollowUps(activities.items, {
        visibleUserIds: visibleUsers.values.map((u) => u.id),
        store,
        viewerId,
        self,
      }),
    [activities, visibleUsers, store, viewerId, self],
  );
  const shown = useMemo(() => filterFollowUps(inScope, filter), [inScope, filter]);
  const groups = useMemo(
    () => followUpGroups(shown, { now, store, users: usersById, visitResults: resultsById }),
    [shown, now, store, usersById, resultsById],
  );

  const people = useMemo(() => pickerUsers(visibleUsers.values), [visibleUsers]);
  const reps = useMemo(
    () => repOptions({ people, list: inScope, users: usersById, viewerId, selected: rep }),
    [people, inScope, usersById, viewerId, rep],
  );
  const types = useMemo(() => typeOptions(inScope, type), [inScope, type]);

  return (
    <>
      <PageHeader title={title} subtitle={inScope.length > 0 ? followUpSummary(groups) : undefined}>
        {inScope.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {can.isManager && (
              <RepFilterMenu
                options={reps}
                value={rep}
                label={repFilterLabel(rep, viewerId, usersById)}
                onSelect={(value) => update({ rep: value })}
              />
            )}
            <TypeFilterMenu options={types} value={type} onSelect={(value) => update({ type: value })} />
            {filtered && (
              <Button variant="ghost" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        )}
      </PageHeader>

      {inScope.length === 0 ? (
        <EmptyState
          icon={<CalendarCheck />}
          color="#2673E6"
          title="Nothing scheduled"
          message={
            can.isManager
              ? 'Follow-up visits, calls, meetings and tasks your team schedules in the Ground Team app show up here.'
              : 'Follow-up visits, calls and tasks you schedule in the Ground Team app show up here.'
          }
        />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={<SearchX />}
          title="No matches"
          message="Nothing scheduled matches these filters."
          action={
            <Button variant="secondary" onClick={clearFilters}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-5 pb-8">
          {groups.map((g) => (
            // Keyed by the filter too, so "Show more" starts over when the list changes underneath it.
            <GroupSection key={`${g.id}:${rep ?? ''}:${type ?? ''}`} group={g} />
          ))}
        </div>
      )}
    </>
  );
}

// A plain section under its h2, like "Needs attention"'s blocks: count pill, then the rows or one line when empty.
function GroupSection({ group }: { group: FollowUpGroup }) {
  const [limit, setLimit] = useState(GROUP_PAGE);
  const n = group.rows.length;
  const look = GROUP_LOOK[group.id];
  const headingId = `follow-ups-${group.id}`;
  return (
    <section aria-labelledby={headingId} className="flex min-w-0 flex-col gap-2">
      <header className="flex items-center gap-2.5 px-1">
        <IconBadge icon={look.icon} color={n > 0 ? look.color : '#8C919E'} size={26} />
        <h2 id={headingId} className="text-sm font-semibold text-fg">
          {group.title}
          <span className="sr-only">, {formatGrouped(n)}</span>
        </h2>
        <span
          aria-hidden
          className={cn('rounded-full px-1.5 text-[11px] font-bold tabular-nums', n > 0 ? 'bg-surface-3 text-fg' : 'text-fg-subtle')}
        >
          {formatGrouped(n)}
        </span>
      </header>
      <div className={cn(cardClass, 'min-w-0 overflow-hidden')}>
        {n === 0 ? (
          <p className="px-4 py-3 text-sm text-fg-muted">{FOLLOW_UP_GROUP_EMPTY[group.id]}</p>
        ) : (
          <ul className="divide-y divide-line">
            {group.rows.slice(0, limit).map((row) => (
              <FollowUpItem key={row.id} row={row} />
            ))}
          </ul>
        )}
        {n > limit && (
          <div className="flex justify-center border-t border-line py-2">
            <Button variant="ghost" onClick={() => setLimit((l) => l + GROUP_PAGE)}>
              Show {formatGrouped(Math.min(GROUP_PAGE, n - limit))} more of {formatGrouped(n - limit)}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

function FollowUpItem({ row }: { row: FollowUpRow }) {
  const Icon = ACTIVITY_ICONS[row.type] ?? ACTIVITY_ICONS.note;
  return (
    <li className="flex min-w-0 items-start gap-3 px-4 py-3">
      <IconBadge icon={<Icon />} color={row.tint} size={32} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-fg">{row.title}</span>
          {row.newer && <NewerAppBadge />}
        </span>
        {row.href ? (
          <Link to={row.href} className="block w-fit max-w-full truncate text-sm text-link hover:underline">
            {row.storeLabel}
          </Link>
        ) : (
          <span className="truncate text-sm text-fg-subtle">{row.storeLabel}</span>
        )}
        {row.notes && <p className="truncate text-xs text-fg-muted">{row.notes}</p>}
      </div>
      <div className="flex max-w-[45%] shrink-0 flex-col items-end gap-1 text-right">
        <span className={cn('text-xs font-semibold tabular-nums', row.overdue ? 'text-danger-text' : 'text-fg')}>
          {row.dueText}
        </span>
        {row.overdueText && <span className="text-[11px] text-danger-text">{row.overdueText}</span>}
        <OwnerAvatar user={row.user} showName size="xs" className="max-w-[10rem] text-xs" />
      </div>
    </li>
  );
}

// The two header menus, built like the pipeline's owner menu (BoardMenus.OwnerFilterMenu).

function RepFilterMenu({
  options,
  value,
  label,
  onSelect,
}: {
  options: readonly RepOption[];
  value: string | null;
  label: string;
  onSelect: (value: string | null) => void;
}) {
  const items = useMemo<MenuEntry[]>(() => {
    const out: MenuEntry[] = [];
    let peopleStarted = false;
    for (const o of options) {
      if (o.kind === 'person' && !peopleStarted) {
        peopleStarted = true;
        out.push({ type: 'separator', id: 'sep-people' }, { type: 'label', id: 'label-people', label: 'People' });
      }
      if (o.kind === 'unassigned') out.push({ type: 'separator', id: 'sep-unassigned' });
      const checked =
        o.value === null ? value === null : o.value === UNASSIGNED_REP ? value === UNASSIGNED_REP : sameId(o.value, value);
      out.push({
        id: o.value ?? 'everyone',
        label: o.label,
        textValue: o.label,
        description: o.inactive ? 'Inactive' : undefined,
        checked,
        onSelect: () => onSelect(o.value),
      });
    }
    return out;
  }, [options, value, onSelect]);

  return (
    <DropdownMenu
      label="Filter by rep"
      minWidth={220}
      items={items}
      trigger={
        <Button
          variant={value !== null ? 'primary' : 'secondary'}
          icon={<ListFilter />}
          iconRight={<ChevronDown />}
          aria-label={`Rep: ${label}. Filter by rep`}
        >
          <span className="max-w-[12rem] truncate">{label}</span>
        </Button>
      }
    />
  );
}

function TypeFilterMenu({
  options,
  value,
  onSelect,
}: {
  options: readonly ActivityType[];
  value: ActivityType | null;
  onSelect: (value: ActivityType | null) => void;
}) {
  const items = useMemo<MenuEntry[]>(
    () => [
      { id: 'all', label: 'All types', checked: value === null, onSelect: () => onSelect(null) },
      { type: 'separator', id: 'sep-types' },
      ...options.map((t) => {
        const Icon = ACTIVITY_ICONS[t] ?? ACTIVITY_ICONS.note;
        return {
          id: t,
          label: ACTIVITY_TYPE_PLURALS[t],
          icon: <Icon aria-hidden />,
          checked: value === t,
          onSelect: () => onSelect(t),
        };
      }),
    ],
    [options, value, onSelect],
  );
  const label = typeFilterLabel(value);

  return (
    <DropdownMenu
      label="Filter by type"
      minWidth={200}
      items={items}
      trigger={
        <Button
          variant={value !== null ? 'primary' : 'secondary'}
          icon={<ListFilter />}
          iconRight={<ChevronDown />}
          aria-label={`Type: ${label}. Filter by type`}
        >
          {label}
        </Button>
      }
    />
  );
}
